import { LocationProps } from "@types";
import profileActions from "actions/profile";
import AtiSuAuthButton from "components/auth/atiSuAuthButton";
import Footer from "components/shared/layout/footer";
import Loader from "components/shared/loader";
import { tokenProviderTypesEnum } from "helpers/enums";
import { paths } from "helpers/paths";
import { useRouter } from "hooks/useRouter";
import Tooltip from "rc-tooltip";
import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import { Action, ActionFunctionAny } from "redux-actions";
import { generateToken, loginTwoFactor } from "sources/account";

const Login = (props: MapDispatchToPropsType & { location: LocationProps }) => {
    const [state, setState] = useState({ userName: "", password: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [twoFaData, setTwoFaData] = useState<{
        accessToken?: { token: string };
        twoFactorProvider?: string;
        requiresTwoFactor?: boolean;
    }>({});
    const [code, setCode] = useState("");
    const router = useRouter();

    const responseHandler = (res: any) => {
        if (res && res.status !== "error") {
            if (res.requiresTwoFactor) {
                setLoading(false);
                return setTwoFaData(res);
            }
            props.getInitialProfile(res);
            const { state } = props.location;
            router.push(state?.from && state?.from !== "/" ? state.from : paths.root);
        } else {
            setError(res?.message || "Неверный логин или пароль.");
        }
        setLoading(false);
    };

    const login = (e: React.FormEvent<HTMLFormElement>) => {
        setError("");
        e.preventDefault();
        setLoading(true);
        generateToken(state).then(responseHandler);
    };
    const loginTwoFa = (e: React.FormEvent<HTMLFormElement>) => {
        setError("");
        e.preventDefault();
        setLoading(true);
        loginTwoFactor({ code, accessToken: twoFaData?.accessToken }).then(responseHandler);
    };

    const codePlaceholder = useMemo(() => {
        switch (twoFaData.twoFactorProvider) {
            case tokenProviderTypesEnum.Email:
                return "Введите код отправленный на почту";
            case tokenProviderTypesEnum.Phone:
                return "Введите код отправленный в SMS";
            case tokenProviderTypesEnum.Authenticator:
                return "Введите код из Google Authenticator";
            default:
                return "";
        }
    }, [twoFaData.twoFactorProvider]);

    return (
        <div className="no-overflow h-100">
            <div className="identity-container">
                {loading && (
                    <div className="loader-snippet--disable-loader">
                        <Loader />
                    </div>
                )}
                <div className="identity-container__shadow" />
                <div className="identity-container__background" />
                <div className="identity-panel identity-panel--small">
                    <div className="identity-column">
                        <h1 className="identity-container__title" />
                        <div className="identity-column__title">Войти в систему</div>
                        {twoFaData.requiresTwoFactor ? (
                            <form className="identity-form" onSubmit={loginTwoFa}>
                                <div className="identity-field">
                                    <div className="col-12">
                                        <input
                                            autoFocus
                                            required
                                            name="code"
                                            className="form-control form-control-sm"
                                            value={code}
                                            onChange={(e) => setCode(e.target.value)}
                                            placeholder={codePlaceholder}
                                        />
                                        <span className="identity-field__error field-validation-error">{error}</span>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <button
                                        className="identity-form__button identity-form__button--yellow"
                                        type="submit"
                                    >
                                        Проверить код
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <form className="identity-form" onSubmit={login}>
                                <div className="identity-field">
                                    <div className="col-12">
                                        <input
                                            required
                                            className="form-control form-control-sm"
                                            type="email"
                                            value={state.userName}
                                            onChange={(e) => setState({ ...state, userName: e.target.value })}
                                            placeholder="Введите почту"
                                        />
                                        <span className="identity-field__error field-validation-error">{error}</span>
                                    </div>
                                </div>
                                <div className="identity-field mb-2">
                                    <div className="col-12">
                                        <input
                                            required
                                            className="form-control form-control-sm"
                                            type="password"
                                            value={state.password}
                                            onChange={(e) => setState({ ...state, password: e.target.value })}
                                            placeholder="Введите пароль"
                                        />
                                    </div>
                                </div>
                                <div className="identity-field col-12">
                                    <a
                                        className="identity-form__link cursor-pointer"
                                        onClick={() => router.push(paths.forgotPassword)}
                                    >
                                        Забыли пароль?
                                    </a>
                                </div>
                                <div className="col-12">
                                    <button
                                        className="identity-form__button identity-form__button--yellow"
                                        type="submit"
                                    >
                                        Войти
                                    </button>
                                </div>
                                <div className="identity-form__buttons-delimeter">
                                    <div className="identity-form__buttons-delimeter--line" />
                                    <span className="identity-form__buttons-delimeter--text">или</span>
                                    <div className="identity-form__buttons-delimeter--line" />
                                </div>
                                <div className="col-12">
                                    <Tooltip
                                        overlay={
                                            <div className="text-center">
                                                Войти через ATI.SU можно, только если <br />
                                                вы прошли регистрацию в CARGO.RUN через ATI.SU.
                                            </div>
                                        }
                                        placement="top"
                                    >
                                        <div>
                                            <AtiSuAuthButton metricName="LoginAtiSuButtonClicked">
                                                Войти через ATI.SU
                                            </AtiSuAuthButton>
                                        </div>
                                    </Tooltip>
                                </div>
                                <div className="identity-form__legal col-12">
                                    Продолжая, вы соглашаетесь с{" "}
                                    <a href="https://cargorun.ru/license-agreement" target="_blank" rel="noreferrer">
                                        Лицензионным соглашением
                                    </a>{" "}
                                    и подтверждаете, что ознакомлены с{" "}
                                    <a href="https://cargorun.ru/privacy" target="_blank" rel="noreferrer">
                                        Политикой обработки персональных данных
                                    </a>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
            <Footer isLogin />
        </div>
    );
};

const mapDispatchToProps = (dispatch: ActionFunctionAny<Action<Object>>) => ({
    getInitialProfile: (params: {}) => dispatch(profileActions.getInitialProfile(params)),
});

type MapDispatchToPropsType = ReturnType<typeof mapDispatchToProps>;

export default connect<{}, MapDispatchToPropsType>(() => ({}), mapDispatchToProps)(Login);
